import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import OfficerDashboard from './pages/OfficerDashboard';
import Navbar from './components/Navbar';
import LoadingScreen from './components/LoadingScreen';
import { AuthProvider, AuthContext } from './context/AuthContext';
import './App.css';

// PrivateRoute with role check
function PrivateRoute({ children, roles }) {
  const { user, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If roles are passed, check if user role matches
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" />; // redirect unauthorized users
  }

  return children;
}

// Helper: Decide which dashboard to load based on role
function RoleBasedDashboard() {
  const { user, isLoading } = useContext(AuthContext);

  if (isLoading) {
    return <LoadingScreen />;
  }

  switch (user.role) {
    case 'admin':
      return <Dashboard />;
    case 'officer':
      return <OfficerDashboard />;
    default:
      return <Navigate to="/login" />;
  }
}

function AppContent() {
  const { isLoading, user } = useContext(AuthContext);

  if (isLoading) {
    return <LoadingScreen userRole={user?.role} />;
  }

  return (
    <Router>
      {user && <Navbar />}
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={<Login />} />

        {/* Default redirect after login */}
        <Route
          path="/"
          element={
            <PrivateRoute roles={['admin', 'officer']}>
              <RoleBasedDashboard />
            </PrivateRoute>
          }
        />

        {/* Admin-specific pages */}
        <Route
          path="/admin"
          element={
            <PrivateRoute roles={['admin']}>
              <AdminPanel />
            </PrivateRoute>
          }
        />

        {/* Officer Dashboard */}
        <Route
          path="/officer"
          element={
            <PrivateRoute roles={['officer']}>
              <OfficerDashboard />
            </PrivateRoute>
          }
        />

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;